import React, { useState } from 'react';

const NOTIFICATION_TYPES = [
  { key: 'bookingConfirmation', label: 'Bokningsbekräftelse (mejl)' },
  { key: 'bookingSmsConfirmation', label: 'Bokningsbekräftelse (SMS)' },
  { key: 'completionReminder', label: 'Kompletteringspåminnelser' },
  { key: 'preCourseInfo', label: 'Förhandsinformation (14 dagar)' },
  { key: 'attendanceLink', label: 'Närvarolänk till lärare' },
  { key: 'diplomaSurvey', label: 'Diplom, LinkedIn & enkät' },
  { key: 'surveyReminder', label: 'Enkätpåminnelser' },
  { key: 'teamsMeetingReminder', label: 'Teams-påminnelse (16 dagar)' },
];

function InstanceTab({ instances, isAdmin, onEdit, onDelete, onNew, onBook, onViewSurvey, onLockAttendance }) {
  const [statusFilter, setStatusFilter] = useState('upcoming');

  const filteredInstances = statusFilter === 'upcoming'
    ? instances.filter(i => i.status !== 'Completed' && i.status !== 'Cancelled')
    : statusFilter === 'all'
    ? instances
    : instances.filter(i => i.status === statusFilter);

  return (
    <>
      <header className="courses-header">
        <div>
          <h1>{isAdmin ? 'Kurstillfällen' : 'Kommande kurser'}</h1>
          <p>{isAdmin ? 'Hantera kommande kurstillfällen' : 'Utveckla dina färdigheter med våra populära kurser'}</p>
        </div>
        <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
          {isAdmin && (
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd'}}
            >
              <option value="upcoming">Kommande</option>
              <option value="Completed">Genomförda</option>
              <option value="Cancelled">Inställda</option>
              <option value="all">Alla</option>
            </select>
          )}
          {isAdmin && (
            <button className="btn btn-primary" onClick={onNew}>
              + Nytt kurstillfälle
            </button>
          )}
        </div>
      </header>

      <div className="courses-table-container">
        <table className="courses-table">
          <thead>
            <tr>
              <th>Kurs</th>
              <th>Datum</th>
              <th>Tid</th>
              <th>Plats</th>
              <th>Utbildare</th>
              <th>Platser</th>
              <th>Status</th>
              <th>Åtgärder</th>
            </tr>
          </thead>
          <tbody>
            {filteredInstances.map(instance => (
              <tr key={instance.id}>
                <td className="course-name-cell">{instance.course?.name || instance.courseName}</td>
                <td>{new Date(instance.startDate).toLocaleDateString('sv-SE')}</td>
                <td>{instance.startTime} - {instance.endTime}</td>
                <td>{instance.location || instance.venue?.name || '-'}</td>
                <td>{instance.instructorName}</td>
                <td>
                  {instance.availability === 'available' && (
                    <span className="seats-available">
                      {instance.maxParticipants - (instance.confirmedBookings || 0)}/{instance.maxParticipants}
                    </span>
                  )}
                  {instance.availability === 'few-seats' && (
                    <span className="seats-few">
                      {instance.maxParticipants - (instance.confirmedBookings || 0)}/{instance.maxParticipants}
                    </span>
                  )}
                  {instance.availability === 'full' && (
                    <span className="seats-full">Fullbokad</span>
                  )}
                </td>
                <td>
                  <span className={`status-badge ${instance.status === 'Confirmed' || instance.status === 'Completed' ? 'active' : instance.status === 'Cancelled' ? 'inactive' : ''}`}>
                    {instance.status === 'Planned' ? 'Planerad' :
                     instance.status === 'Confirmed' ? 'Bekräftad' :
                     instance.status === 'Full' ? 'Fullbokad' :
                     instance.status === 'Cancelled' ? 'Inställd' :
                     instance.status === 'Completed' ? 'Genomförd' : instance.status}
                  </span>
                </td>
                <td>
                  {isAdmin ? (
                    <div className="table-actions">
                      <button className="btn btn-secondary btn-small" onClick={() => onEdit(instance)}>
                        Redigera
                      </button>
                      {instance.status === 'Completed' && onViewSurvey && (
                        <button className="btn btn-secondary btn-small" onClick={() => onViewSurvey(instance)}>
                          Enkät
                        </button>
                      )}
                      {!instance.attendanceLockedAt && new Date(instance.startDate) <= new Date() && onLockAttendance && (
                        <button className="btn btn-primary btn-small" onClick={() => onLockAttendance(instance)}
                          title="Markera alla som närvarande och skicka diplom/enkät">
                          Lås närvaro
                        </button>
                      )}
                      <button className="btn btn-danger btn-small" onClick={() => onDelete(instance)}>
                        Ta bort
                      </button>
                    </div>
                  ) : (
                    instance.availability !== 'full' ? (
                      <button className="btn btn-primary btn-small" onClick={() => onBook(instance)}>
                        Boka
                      </button>
                    ) : (
                      <button className="btn btn-secondary btn-small" onClick={() => onBook(instance)}>
                        Väntelista
                      </button>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredInstances.length === 0 && (
          <div className="no-data">
            <p>{statusFilter === 'Completed' ? 'Inga genomförda kurstillfällen.' : 'Inga kurstillfällen matchar filtret.'}</p>
            {isAdmin && statusFilter === 'upcoming' && <p>Klicka på "+ Nytt kurstillfälle" för att skapa ett.</p>}
          </div>
        )}
      </div>
    </>
  );
}

function InstanceModal({ show, editing, form, setForm, courses, venues, companies = [], onSubmit, onClose, onCreateCompany, onCreateVenue, error }) {
  if (!show) return null;

  const selectedVenue = form.venueId ? venues.find(v => v.id === parseInt(form.venueId)) : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <h2>{editing ? 'Redigera kurstillfälle' : 'Skapa nytt kurstillfälle'}</h2>
        {error && <div className="alert alert-danger" style={{marginBottom: '15px'}}>{error}</div>}
        <form onSubmit={onSubmit}>
          <div className="form-group">
            <label>Kurs *</label>
            <select
              value={form.courseId}
              onChange={(e) => setForm({...form, courseId: e.target.value})}
              required
            >
              <option value="">Välj kurs...</option>
              {courses.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Startdatum *</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({...form, startDate: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Slutdatum</label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({...form, endDate: e.target.value})}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Starttid</label>
              <input
                type="time"
                value={form.startTime}
                onChange={(e) => setForm({...form, startTime: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Sluttid</label>
              <input
                type="time"
                value={form.endTime}
                onChange={(e) => setForm({...form, endTime: e.target.value})}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Lokal (välj från lista)</label>
            <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
              <select
                value={form.venueId}
                onChange={(e) => {
                  const venueId = e.target.value;
                  const venue = venues.find(v => v.id === parseInt(venueId));
                  setForm({
                    ...form,
                    venueId,
                    location: venue ? venue.name : form.location,
                    city: venue ? venue.city : form.city,
                    address: venue ? (venue.address || '') : form.address
                  });
                }}
                style={{flex: 1}}
              >
                <option value="">Välj lokal eller ange manuellt nedan...</option>
                {venues.map(v => (
                  <option key={v.id} value={v.id}>{v.name} - {v.city}</option>
                ))}
              </select>
              {onCreateVenue && (
                <button type="button" className="btn btn-secondary btn-small" onClick={onCreateVenue}>
                  + Ny
                </button>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Lokal (manuellt) *</label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => setForm({...form, location: e.target.value})}
                placeholder="T.ex. Best Western Kom Hotel"
                required={!form.venueId}
              />
            </div>
            <div className="form-group">
              <label>Stad *</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => setForm({...form, city: e.target.value})}
                placeholder="T.ex. Stockholm"
                required={!form.venueId}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Adress</label>
            <input
              type="text"
              value={form.address}
              onChange={(e) => setForm({...form, address: e.target.value})}
              placeholder="T.ex. Döbelnsgatan 17"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Lokalkostnad (kr)</label>
              <input
                type="number"
                value={form.venueCost}
                onChange={(e) => setForm({...form, venueCost: e.target.value})}
                placeholder="T.ex. 3500"
              />
            </div>
            <div className="form-group">
              <label>Beställt av (företag/lärare)</label>
              <input
                type="text"
                value={form.venuePaidBy}
                onChange={(e) => setForm({...form, venuePaidBy: e.target.value})}
                placeholder="T.ex. Ejbe Vision AB"
              />
            </div>
          </div>

          {selectedVenue && form.startDate && (
            <div className="venue-cancellation-info">
              <span className="meta-icon">⚠️</span>
              <span>
                Sista avbokning av lokal: {new Date(
                  new Date(form.startDate).getTime() -
                  (selectedVenue.cancellationDays || 14) * 24 * 60 * 60 * 1000
                ).toLocaleDateString('sv-SE')}
              </span>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Utbildare *</label>
              <input
                type="text"
                value={form.instructorName}
                onChange={(e) => setForm({...form, instructorName: e.target.value})}
                required
                placeholder="T.ex. Daniel Karlsson"
              />
            </div>
            <div className="form-group">
              <label>Utbildarens e-post</label>
              <input
                type="email"
                value={form.instructorEmail}
                onChange={(e) => setForm({...form, instructorEmail: e.target.value})}
                placeholder="daniel@projektledarpodden.se"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Max deltagare</label>
              <input
                type="number"
                value={form.maxParticipants}
                onChange={(e) => setForm({...form, maxParticipants: e.target.value})}
                min="1"
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({...form, status: e.target.value})}
              >
                <option value="Planned">Planerad</option>
                <option value="Confirmed">Bekräftad</option>
                <option value="Full">Fullbokad</option>
                <option value="Cancelled">Inställd</option>
                <option value="Completed">Genomförd</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Pris (kr)</label>
              <input
                type="number"
                value={form.price}
                onChange={(e) => setForm({...form, price: e.target.value})}
                placeholder="Lämna tomt för standardpris"
              />
            </div>
            <div className="form-group">
              <label>Early bird-pris (kr)</label>
              <input
                type="number"
                value={form.earlyBirdPrice}
                onChange={(e) => setForm({...form, earlyBirdPrice: e.target.value})}
                placeholder="T.ex. 3995"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Early bird deadline</label>
            <input
              type="date"
              value={form.earlyBirdDeadline}
              onChange={(e) => setForm({...form, earlyBirdDeadline: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label>Teams-länk (för onlinekurser)</label>
            <input
              type="url"
              value={form.teamsLink}
              onChange={(e) => setForm({...form, teamsLink: e.target.value})}
              placeholder="https://teams.microsoft.com/..."
            />
          </div>

          <div className="form-group">
            <label>Länk till kursmaterial</label>
            <input
              type="url"
              value={form.courseMaterialLink}
              onChange={(e) => setForm({...form, courseMaterialLink: e.target.value})}
              placeholder="https://..."
            />
          </div>

          <div className="form-group">
            <label>Extern utvärderingslänk (t.ex. Microsoft Forms)</label>
            <input
              type="url"
              value={form.evaluationLink}
              onChange={(e) => setForm({...form, evaluationLink: e.target.value})}
              placeholder="https://forms.microsoft.com/..."
            />
          </div>

          <div className="form-row">
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  onChange={(e) => setForm({...form, isPublished: e.target.checked})}
                />
                Publicera på publik bokningssida
              </label>
            </div>
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={form.isInternal}
                  onChange={(e) => setForm({...form, isInternal: e.target.checked})}
                />
                Intern företagskurs (visas aldrig publikt)
              </label>
            </div>
          </div>

          {form.isInternal && (
            <div className="form-row" style={{backgroundColor: '#f0f7ff', padding: '15px', borderRadius: '8px', marginBottom: '15px'}}>
              <h4 style={{margin: '0 0 10px 0', color: '#2563eb'}}>Företagsinterna kursuppgifter</h4>
              <div className="form-group">
                <label>Företag (portal)</label>
                <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                  <select
                    value={form.companyId || ''}
                    onChange={(e) => {
                      const companyId = e.target.value;
                      const company = companies.find(c => c.id === parseInt(companyId));
                      setForm({...form, companyId: companyId, corporateCompany: company ? company.name : form.corporateCompany });
                    }}
                    style={{flex: 1}}
                  >
                    <option value="">— Välj företag (valfritt) —</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}{c.portalSlug ? ` (${c.portalSlug})` : ''}</option>
                    ))}
                  </select>
                  {onCreateCompany && (
                    <button type="button" className="btn btn-secondary btn-small" onClick={onCreateCompany}>
                      + Nytt
                    </button>
                  )}
                </div>
                {form.companyId && companies.find(c => c.id === parseInt(form.companyId))?.portalSlug && (
                  <small style={{color: '#2563eb', marginTop: '4px', display: 'block'}}>
                    Portal: <a href={`https://boka.projektledarpodden.se/portal/${companies.find(c => c.id === parseInt(form.companyId)).portalSlug}`} target="_blank" rel="noreferrer">
                      boka.projektledarpodden.se/portal/{companies.find(c => c.id === parseInt(form.companyId)).portalSlug}
                    </a>
                  </small>
                )}
              </div>
              <div className="form-group">
                <label>Företagsnamn (fritext)</label>
                <input
                  type="text"
                  value={form.corporateCompany || ''}
                  onChange={(e) => setForm({...form, corporateCompany: e.target.value})}
                  placeholder="T.ex. Surewood AB"
                />
              </div>
              <div className="form-group">
                <label>Totalt offertpris (kr)</label>
                <input
                  type="number"
                  value={form.corporateTotalPrice || ''}
                  onChange={(e) => setForm({...form, corporateTotalPrice: e.target.value})}
                  placeholder="T.ex. 27000"
                />
                <small style={{color: '#666'}}>Används istället för individuella bokningspriser</small>
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Kundens koordinator (e-post)</label>
            <input
              type="email"
              value={form.coordinatorEmail || ''}
              onChange={(e) => setForm({...form, coordinatorEmail: e.target.value})}
              placeholder="hr@foretaget.se"
            />
            <small style={{color: '#666'}}>Får närvarorapport med diplomlänkar efter kursen (lämna tomt om ej aktuellt)</small>
          </div>

          <div className="form-group">
            <label>Interna anteckningar</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({...form, notes: e.target.value})}
              rows="3"
              placeholder="Anteckningar som endast syns för admin..."
            />
          </div>

          <div className="notification-settings-section">
            <h4>Aviseringsinställningar (överskriver kursmallen)</h4>
            <p className="notification-hint">Välj "Ärv från kursmall" för att använda standardinställningarna.</p>
            <div className="notification-settings-grid instance-grid">
              {NOTIFICATION_TYPES.map(({ key, label }) => (
                <div key={key} className="notification-select-row">
                  <span className="notification-label">{label}</span>
                  <select
                    value={form.notificationSettings?.[key] === null ? 'inherit' : form.notificationSettings?.[key] ? 'enabled' : 'disabled'}
                    onChange={(e) => {
                      const val = e.target.value === 'inherit' ? null : e.target.value === 'enabled';
                      setForm({
                        ...form,
                        notificationSettings: { ...form.notificationSettings, [key]: val }
                      });
                    }}
                  >
                    <option value="inherit">Ärv från kursmall</option>
                    <option value="enabled">Aktiverad</option>
                    <option value="disabled">Inaktiverad</option>
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-success">
              {editing ? 'Spara ändringar' : 'Skapa kurstillfälle'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Avbryt
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export { InstanceTab, InstanceModal };
